'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import { Building2 } from 'lucide-react';
import * as React from 'react';
import { useAuth } from '../auth';
import { useUpdateOrganizationNameMutation } from '../hooks/use-gateway-queries';
import { toast } from '../utils/toast';
import { Button } from './button';
import { InfoSection } from './info-section';

const logger = createLogger('client:component:organization-settings');

type OrganizationSettingsProps = {
  organizationName: string;
  organizationId: string;
  onUpdate?: (newName: string) => void;
};

/**
 * OrganizationSettings component
 *
 * Displays organization details with inline editing for organization name.
 * Only admins can edit the organization name.
 */
export const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({
  organizationName,
  organizationId,
  onUpdate,
}) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedName, setEditedName] = React.useState(organizationName);
  const [error, setError] = React.useState<string | null>(null);

  const updateOrgNameMutation = useUpdateOrganizationNameMutation();

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Reset edited name when organizationName changes
  React.useEffect(() => {
    setEditedName(organizationName);
  }, [organizationName]);

  const handleStartEdit = () => {
    if (!isAdmin) return;
    setIsEditing(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedName(organizationName);
    setError(null);
  };

  const handleSaveEdit = async () => {
    const trimmedName = editedName.trim();

    // Validation
    if (!trimmedName) {
      setError('Organization name cannot be empty');
      return;
    }

    if (trimmedName.length > 100) {
      setError('Organization name cannot exceed 100 characters');
      return;
    }

    if (trimmedName === organizationName) {
      // No change, just exit edit mode
      setIsEditing(false);
      return;
    }

    try {
      setError(null);
      logger.info('🔄 [ORG-SETTINGS] Updating organization name:', {
        oldName: organizationName,
        newName: trimmedName,
      });

      const response = await updateOrgNameMutation.mutateAsync({
        body: { name: trimmedName },
      });

      if (response.status === 200 && response.body.success) {
        logger.info('✅ [ORG-SETTINGS] Organization name updated successfully');
        toast.success('Organization Updated', `Organization name changed to "${trimmedName}"`);

        // Wait a brief moment to ensure the name is updated in the cache
        setTimeout(() => {
          setIsEditing(false);
          onUpdate?.(trimmedName);
        }, 100);
      } else {
        throw new Error('Failed to update organization name');
      }
    } catch (err) {
      logger.error('❌ [ORG-SETTINGS] Failed to update organization name:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update organization name';
      setError(errorMessage);
      toast.error('Update Failed', errorMessage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <InfoSection title="Organization Settings" titleIcon={<Building2 className="w-5 h-5" />}>
      <div className="space-y-4">
        {/* Organization Name */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-muted-foreground">Organization Name</label>

          {isEditing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={editedName}
                onChange={e => setEditedName(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={updateOrgNameMutation.isPending}
                className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter organization name"
                autoFocus
                maxLength={100}
              />

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSaveEdit}
                  disabled={updateOrgNameMutation.isPending}
                  variant="primary"
                  size="sm"
                >
                  {updateOrgNameMutation.isPending && <span className="animate-spin">⟳</span>}
                  {updateOrgNameMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  disabled={updateOrgNameMutation.isPending}
                  variant="secondary"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-base font-medium">{organizationName}</p>
                {updateOrgNameMutation.isPending && (
                  <span className="text-sm text-muted-foreground animate-spin">⟳</span>
                )}
              </div>
              {isAdmin && (
                <Button
                  onClick={handleStartEdit}
                  disabled={updateOrgNameMutation.isPending}
                  variant="ghost"
                  size="sm"
                  title="Edit organization name"
                >
                  Edit
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Organization ID (read-only) */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-muted-foreground">Organization ID</label>
          <p className="text-secondary-enhanced text-sm font-mono glass-surface-bg glass-surface-border px-3 py-2 rounded-lg break-all">
            {organizationId}
          </p>
        </div>

        {!isAdmin && (
          <p className="text-secondary-enhanced text-sm pt-2 border-t border-border/30">
            Only administrators can edit organization settings.
          </p>
        )}
      </div>
    </InfoSection>
  );
};

OrganizationSettings.displayName = 'OrganizationSettings';
